import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';

/* ─── tiny helpers ──────────────────────────────────────────────────────────── */
const fmt  = (n) => parseFloat(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtD = (d) => { if (!d) return '—'; const dt = new Date(d); return dt.toLocaleDateString('fr-FR', { year:'numeric', month:'short', day:'2-digit' }); };
const fmtT = (d) => { if (!d) return ''; return new Date(d).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' }); };
const statusClass = (s) => {
  switch (s) {
    case 'Paid':      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'Partial':   return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Refunded':  return 'bg-purple-100 text-purple-700 border-purple-200';
    default:          return 'bg-red-100 text-red-700 border-red-200';
  }
};

/* ─── Print helpers ──────────────────────────────────────────────────────────── */
function printInvoice(invoice, customer, companyName = 'Royal Pips ERP') {
  const items = (invoice.items || []).map((it, i) =>
    `<tr style="background:${i%2===0?'#f8fafc':'#fff'}">
      <td style="padding:6px 10px">${it.product_name}</td>
      <td style="padding:6px 10px;text-align:center">${fmt(it.quantity)} ${it.unit||''}</td>
      <td style="padding:6px 10px;text-align:right">CFA ${fmt(it.unit_price)}</td>
      <td style="padding:6px 10px;text-align:right">${it.discount>0?'CFA '+fmt(it.discount):'—'}</td>
      <td style="padding:6px 10px;text-align:right;font-weight:700">CFA ${fmt(it.price_ttc)}</td>
    </tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${invoice.invoice_number}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:11px;padding:20px;color:#1e293b}
    @page{size:A4;margin:10mm}
    .header{border-bottom:3px solid #3730a3;padding-bottom:12px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-start}
    .company{font-size:22px;font-weight:900;color:#3730a3;text-transform:uppercase}
    .inv-num{font-size:18px;font-weight:700;color:#1e293b}
    .meta{background:#f1f5f9;border-radius:6px;padding:10px;margin-bottom:14px;display:grid;grid-template-columns:1fr 1fr;gap:6px;font-size:11px}
    .meta b{color:#3730a3}
    table{border-collapse:collapse;width:100%;margin-bottom:14px}
    th{padding:8px 10px;text-align:left;background:#3730a3;color:#fff;font-size:10px;text-transform:uppercase}
    .totals{text-align:right;border-top:2px solid #3730a3;padding-top:10px}
    .total-row{display:flex;justify-content:flex-end;gap:40px;margin:2px 0}
    .grand{font-size:16px;font-weight:900;color:#3730a3;margin-top:6px}
    .footer{margin-top:24px;border-top:1px solid #e2e8f0;padding-top:10px;display:flex;justify-content:space-between;font-size:10px;color:#64748b}
    .sig-box{width:160px;border-top:1px solid #1e293b;text-align:center;padding-top:4px;font-size:10px;margin-top:40px}
    </style></head><body>
    <div class="header">
      <div><div class="company">${companyName}</div><div style="color:#64748b;font-size:11px;margin-top:4px">Customer Receipt</div></div>
      <div style="text-align:right"><div class="inv-num">${invoice.invoice_number}</div>
        <div style="color:#64748b;font-size:11px">${fmtD(invoice.created_at)} ${fmtT(invoice.created_at)}</div>
        <div style="margin-top:4px"><span style="background:${invoice.status==='Paid'?'#d1fae5':invoice.status==='Partial'?'#fef3c7':'#fee2e2'};color:${invoice.status==='Paid'?'#065f46':invoice.status==='Partial'?'#92400e':'#991b1b'};padding:2px 8px;border-radius:12px;font-weight:700;font-size:10px">${invoice.status}</span></div>
      </div>
    </div>
    <div class="meta">
      <div><b>Customer:</b> ${customer?.name||'—'}</div>
      <div><b>Phone:</b> ${customer?.phone||'—'}</div>
      <div><b>Payment:</b> ${invoice.payment_method||'Cash'}</div>
      <div><b>Date:</b> ${fmtD(invoice.created_at)}</div>
    </div>
    <div className="w-full overflow-x-auto max-w-full">
<table><thead><tr><th>Product</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Discount</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>${items}</tbody></table>
</div>
    <div class="totals">
      <div class="total-row"><span>Subtotal HT:</span><span>CFA ${fmt(invoice.total_ht)}</span></div>
      ${invoice.tva_rate>0?`<div class="total-row"><span>TVA (${invoice.tva_rate}%):</span><span>CFA ${fmt(invoice.total_tva)}</span></div>`:''}
      ${invoice.discount_total>0?`<div class="total-row"><span>Discount:</span><span style="color:#dc2626">- CFA ${fmt(invoice.discount_total)}</span></div>`:''}
      <div class="total-row grand"><span>TOTAL TTC:</span><span>CFA ${fmt(invoice.total_ttc||invoice.total_amount)}</span></div>
      <div class="total-row"><span style="color:#059669">Amount Paid:</span><span style="color:#059669;font-weight:700">CFA ${fmt(invoice.amount_paid)}</span></div>
      ${parseFloat(invoice.total_amount)-parseFloat(invoice.amount_paid)>0?`<div class="total-row"><span style="color:#dc2626">Outstanding:</span><span style="color:#dc2626;font-weight:700">CFA ${fmt(parseFloat(invoice.total_amount)-parseFloat(invoice.amount_paid))}</span></div>`:''}
    </div>
    ${invoice.note?`<div style="margin-top:14px;background:#f8fafc;padding:10px;border-left:3px solid #3730a3;border-radius:4px;font-size:11px"><b>Note:</b> ${invoice.note}</div>`:''}
    <div style="display:flex;justify-content:space-between;margin-top:20px">
      <div class="sig-box">Customer Signature</div>
      <div class="sig-box">Authorized By</div>
    </div>
    <div class="footer"><span>Printed: ${new Date().toLocaleString('fr-FR')}</span><span>${companyName} — All rights reserved</span></div>
    <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
    </body></html>`;
  const w = window.open('','_blank','width=900,height=700');
  if (w) { w.document.write(html); w.document.close(); }
  else alert('Please allow popups for printing.');
}

function printCustomerAccount(customer, days) {
  const rows = days.map((d, i) =>
    `<tr style="background:${i%2===0?'#f8fafc':'#fff'}">
      <td style="padding:6px 10px">${fmtD(d.purchase_date)}</td>
      <td style="padding:6px 10px;text-align:center">${d.invoice_count}</td>
      <td style="padding:6px 10px;text-align:right;font-weight:700">CFA ${fmt(d.day_total)}</td>
      <td style="padding:6px 10px;text-align:right;color:#059669">CFA ${fmt(d.day_paid)}</td>
      <td style="padding:6px 10px;text-align:right;color:#dc2626;font-weight:700">CFA ${fmt(d.day_balance)}</td>
      <td style="padding:6px 10px;font-size:10px;color:#64748b">${d.invoice_numbers||'—'}</td>
    </tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Account — ${customer.name}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:11px;padding:20px;color:#1e293b}
    @page{size:A4;margin:10mm}
    .header{border-bottom:3px solid #3730a3;padding-bottom:12px;margin-bottom:16px}
    .company{font-size:18px;font-weight:900;color:#3730a3}
    .stats{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px}
    .stat{background:#f1f5f9;border-radius:6px;padding:10px;text-align:center}
    .stat-val{font-size:16px;font-weight:900;color:#3730a3}
    .stat-lbl{font-size:9px;color:#64748b;text-transform:uppercase;margin-top:2px}
    table{border-collapse:collapse;width:100%}
    th{padding:8px 10px;text-align:left;background:#3730a3;color:#fff;font-size:10px;text-transform:uppercase}
    .footer{margin-top:20px;border-top:1px solid #e2e8f0;padding-top:8px;font-size:10px;color:#64748b;text-align:right}
    </style></head><body>
    <div class="header">
      <div class="company">Royal Pips ERP — Customer Account Statement</div>
      <div style="margin-top:6px;font-size:13px;font-weight:700">${customer.name}</div>
      <div style="font-size:11px;color:#64748b">Phone: ${customer.phone||'—'} | Printed: ${new Date().toLocaleDateString('fr-FR')}</div>
    </div>
    <div class="stats">
      <div class="stat"><div class="stat-val">CFA ${fmt(customer.total_invoiced)}</div><div class="stat-lbl">Total Purchased</div></div>
      <div class="stat"><div class="stat-val">CFA ${fmt(customer.total_paid)}</div><div class="stat-lbl">Total Paid</div></div>
      <div class="stat"><div class="stat-val" style="color:#dc2626">CFA ${fmt(customer.outstanding_balance)}</div><div class="stat-lbl">Outstanding Balance</div></div>
    </div>
    <div className="w-full overflow-x-auto max-w-full">
<table><thead><tr><th>Date</th><th style="text-align:center">Invoices</th><th style="text-align:right">Total</th><th style="text-align:right">Paid</th><th style="text-align:right">Balance</th><th>Invoice Refs</th></tr></thead>
    <tbody>${rows}</tbody></table>
</div>
    <div class="footer">Generated by Royal Pips ERP • ${new Date().toLocaleString('fr-FR')}</div>
    <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
    </body></html>`;
  const w = window.open('','_blank','width=900,height=700');
  if (w) { w.document.write(html); w.document.close(); }
}

/* ─── VIEW CONSTANTS ──────────────────────────────────────────────────────── */
const VIEW_DASHBOARD  = 'dashboard';
const VIEW_LEDGER     = 'ledger';
const VIEW_DAILY      = 'daily';

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════════ */
const Sales = () => {
  const { authFetch, user } = useContext(AppContext);
  const { t } = useTranslation();
  const isAdmin    = user?.role === 'Admin';
  const isManager  = user?.role === 'Manager' || isAdmin;

  /* ── View state ───────────────────────────────────────────────────────── */
  const [view,             setView]             = useState(VIEW_DASHBOARD);
  const [selectedCustomer, setSelectedCustomer] = useState(null);  // { id, name, ... }
  const [ledgerData,       setLedgerData]       = useState(null);  // { customer, days }
  const [selectedDayIdx,   setSelectedDayIdx]   = useState(0);
  const [dailyData,        setDailyData]        = useState(null);  // { customer, date, invoices }

  /* ── Dashboard state ─────────────────────────────────────────────────── */
  const [customers,  setCustomers]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [search,     setSearch]     = useState('');
  const [filter,     setFilter]     = useState('all'); // all | balance | returns

  /* ── Edit invoice state ───────────────────────────────────────────────── */
  const [editOpen,   setEditOpen]   = useState(false);
  const [editInv,    setEditInv]    = useState(null);
  const [editItems,  setEditItems]  = useState([]);
  const [editReason, setEditReason] = useState('');
  const [editNote,   setEditNote]   = useState('');
  const [products,   setProducts]   = useState([]);
  const [prodSearch, setProdSearch] = useState('');

  /* ── Return state ─────────────────────────────────────────────────────── */
  const [returnOpen,   setReturnOpen]   = useState(false);
  const [returnInv,    setReturnInv]    = useState(null);
  const [returnItems,  setReturnItems]  = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [returnType,   setReturnType]   = useState('partial');

  // -- NEW PAY STATES --
  const [isPayOpen, setIsPayOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');

  const [isPayAllOpen, setIsPayAllOpen] = useState(false);
  const [payAllAmount, setPayAllAmount] = useState('');
  const [payAllMethod, setPayAllMethod] = useState('Cash');

  /* ── Ledger date filter ────────────────────────────────────────────── */
  const [ledgerStartDate, setLedgerStartDate] = useState('');
  const [ledgerEndDate,   setLedgerEndDate]   = useState('');

  /* ── Delete confirm ──────────────────────────────────────────────────── */
  const [deleteOpen,  setDeleteOpen]  = useState(false);
  const [deleteInvId, setDeleteInvId] = useState(null);

  /* ── Notification ────────────────────────────────────────────────────── */
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  /* ── Fetch dashboard data ─────────────────────────────────────────────── */
  const fetchDashboard = useCallback(() => {
    setLoading(true);
    setError('');
    authFetch('/api/sales')
      .then(r => r.json())
      .then(d => setCustomers(Array.isArray(d) ? d : []))
      .catch(() => setError('Failed to load customer data'))
      .finally(() => setLoading(false));
  }, [authFetch]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  /* ── Fetch product list for edit modal ────────────────────────────────── */
  useEffect(() => {
    authFetch('/api/products').then(r => r.json()).then(d => setProducts(Array.isArray(d) ? d : [])).catch(() => {});
  }, [authFetch]);

  /* ── Keyboard navigation ──────────────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (editOpen || returnOpen || deleteOpen) return;
      if (e.key === 'Escape') {
        if (view === VIEW_DAILY)   { setView(VIEW_LEDGER); }
        else if (view === VIEW_LEDGER) { setView(VIEW_DASHBOARD); }
      }
      if (view === VIEW_DAILY && ledgerData?.days) {
        if (e.key === 'ArrowLeft')  { goToDay(selectedDayIdx + 1); }
        if (e.key === 'ArrowRight') { goToDay(selectedDayIdx - 1); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  /* ── Open customer ledger ──────────────────────────────────────────────── */
  const openLedger = useCallback((customer) => {
    setLoading(true);
    setError('');
    authFetch(`/api/sales?customer_id=${customer.id}`)
      .then(r => r.json())
      .then(d => {
        setLedgerData(d);
        setSelectedCustomer(d.customer || customer);
        setView(VIEW_LEDGER);
      })
      .catch(() => setError('Failed to load account'))
      .finally(() => setLoading(false));
  }, [authFetch]);

  /* ── Open daily record ────────────────────────────────────────────────── */
  const openDailyRecord = useCallback((dayObj, dayIdx) => {
    setLoading(true);
    setError('');
    authFetch(`/api/sales?customer_id=${selectedCustomer.id}&date=${dayObj.purchase_date}`)
      .then(r => r.json())
      .then(d => {
        setDailyData(d);
        setSelectedDayIdx(dayIdx);
        setView(VIEW_DAILY);
      })
      .catch(() => setError('Failed to load daily record'))
      .finally(() => setLoading(false));
  }, [authFetch, selectedCustomer]);

  /* ── Navigate days ───────────────────────────────────────────────────── */
  const goToDay = (idx) => {
    const days = ledgerData?.days || [];
    if (idx < 0 || idx >= days.length) return;
    openDailyRecord(days[idx], idx);
  };

  /* ── Refresh daily data ──────────────────────────────────────────────── */
  const refreshDaily = useCallback(() => {
    if (!dailyData) return;
    authFetch(`/api/sales?customer_id=${selectedCustomer.id}&date=${dailyData.date}`)
      .then(r => r.json())
      .then(d => setDailyData(d))
      .catch(() => {});
    // Also refresh ledger
    authFetch(`/api/sales?customer_id=${selectedCustomer.id}`)
      .then(r => r.json())
      .then(d => { setLedgerData(d); setSelectedCustomer(d.customer); })
      .catch(() => {});
  }, [authFetch, dailyData, selectedCustomer]);

  /* ── Edit: open ──────────────────────────────────────────────────────── */
  const openEdit = (inv) => {
    setEditInv(inv);
    setEditItems(inv.items.map(it => ({ ...it, _new: false })));
    setEditNote(inv.note || '');
    setEditReason('');
    setProdSearch('');
    setEditOpen(true);
  };

  /* ── Edit: save ──────────────────────────────────────────────────────── */
  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editReason.trim()) { alert('Please enter a reason for the edit.'); return; }
    setLoading(true);
    try {
      const res = await authFetch(`/api/sales/${editInv.id}`, {
        method: 'PUT',
        body: JSON.stringify({ items: editItems, note: editNote, reason: editReason })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setEditOpen(false);
      setEditInv(null);
      showToast('✅ Invoice updated and inventory synced');
      refreshDaily();
      fetchDashboard();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Return: open ─────────────────────────────────────────────────────── */
  const openReturn = (inv) => {
    setReturnInv(inv);
    setReturnItems(inv.items.map(it => ({ ...it, qty_return: 0 })));
    setReturnReason('');
    setReturnType('partial');
    setReturnOpen(true);
  };

  /* ── Return: save ─────────────────────────────────────────────────────── */
  const handleReturnSave = async (e) => {
    e.preventDefault();
    if (!returnReason.trim()) { alert('Please enter a return reason.'); return; }
    const items = returnType === 'full'
      ? returnInv.items.map(it => ({ invoice_item_id: it.id, qty_refunded: it.quantity }))
      : returnItems.filter(it => it.qty_return > 0).map(it => ({ invoice_item_id: it.id, qty_refunded: parseFloat(it.qty_return) }));
    if (returnType === 'partial' && items.length === 0) { alert('Select at least one item to return.'); return; }
    setLoading(true);
    try {
      const res = await authFetch('/api/refunds', {
        method: 'POST',
        body: JSON.stringify({ invoice_id: returnInv.id, type: returnType, items, reason: returnReason })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setReturnOpen(false);
      showToast(`✅ Return processed — CFA ${fmt(data.refund_amount)} refunded & stock restored`);
      refreshDaily();
      fetchDashboard();
    } catch (err) {
      alert('Return failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Delete invoice ───────────────────────────────────────────────────── */
  const handleDelete = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/sales/${deleteInvId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setDeleteOpen(false);
      showToast('🗑️ Invoice deleted and stock restored');
      // If this was the only invoice on this day, go back to ledger
      if ((dailyData?.invoices?.length || 0) <= 1) {
        setView(VIEW_LEDGER);
        openLedger(selectedCustomer);
      } else {
        refreshDaily();
      }
      fetchDashboard();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Dashboard filters ────────────────────────────────────────────────── */
  const filteredCustomers = customers.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.phone || '').includes(search);
    const matchFilter =
      filter === 'all' ? true :
      filter === 'balance' ? parseFloat(c.outstanding_balance) > 0 :
      filter === 'returns' ? parseFloat(c.total_returns) > 0 : true;
    return matchSearch && matchFilter;
  });

  const totalAR = customers.reduce((s, c) => s + parseFloat(c.outstanding_balance || 0), 0);
  const totalInvoiced = customers.reduce((s, c) => s + parseFloat(c.total_invoiced || 0), 0);

  /* ══════════════════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", minHeight: '100vh', position: 'relative' }}>
      {/* Google Font */}
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{ position:'fixed', top:20, right:24, zIndex:9999, background:'#0f172a', color:'#fff', padding:'12px 20px', borderRadius:10, boxShadow:'0 8px 32px rgba(0,0,0,0.3)', fontSize:13, fontWeight:600, animation:'fadeIn .3s ease' }}>
          {toast}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          VIEW 1 — CUSTOMER ACCOUNT DASHBOARD
      ════════════════════════════════════════════════════════════════ */}
      {view === VIEW_DASHBOARD && (
        <div className="space-y-6">
          <header className="flex justify-between items-start mb-2">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <span className="text-3xl">📊</span> Customer Sales Ledger
              </h2>
              <p className="text-gray-500 mt-1">Complete customer account management & transaction history</p>
            </div>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl p-5 shadow-md">
              <p className="text-sm font-medium opacity-80">Total Outstanding</p>
              <p className="text-3xl font-bold mt-1">CFA {fmt(totalAR)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-5 shadow-md">
              <p className="text-sm font-medium opacity-80">Total Invoiced</p>
              <p className="text-3xl font-bold mt-1">CFA {fmt(totalInvoiced)}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-5 shadow-md">
              <p className="text-sm font-medium opacity-80">Accounts</p>
              <p className="text-3xl font-bold mt-1">{customers.length}</p>
            </div>
          </div>

          {/* Search + Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search customer name or phone..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none text-sm bg-white shadow-sm"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            </div>
            {['all','balance','returns'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg border font-bold text-sm transition-all ${
                  filter === f ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}>
                {f === 'all' ? 'All Customers' : f === 'balance' ? '💰 With Balance' : '↩️ With Returns'}
              </button>
            ))}
          </div>

          {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm font-bold border border-red-200">{error}</div>}

          {loading && <div className="text-center py-16 text-gray-400">Loading accounts…</div>}

          {/* Customer Cards Grid */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredCustomers.length === 0 && (
                <div className="col-span-full text-center py-16 text-gray-400">
                  No customers found{search ? ` for "${search}"` : ''}.
                </div>
              )}
              {filteredCustomers.map(c => {
                const balance = parseFloat(c.outstanding_balance || 0);
                const isOverdue = balance > 0;
                return (
                  <div key={c.id}
                    onClick={() => openLedger(c)}
                    className={`bg-white rounded-xl border ${isOverdue ? 'border-red-200' : 'border-gray-200'} p-5 cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg hover:border-indigo-400 group`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{c.name}</div>
                        <div className="text-gray-500 text-xs mt-1">📞 {c.phone || 'No phone'}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {c.invoice_count > 0 && <span className="bg-indigo-50 text-indigo-700 rounded-full px-2.5 py-0.5 text-[10px] font-bold">{c.invoice_count} invoices</span>}
                        {parseFloat(c.total_returns) > 0 && <span className="bg-pink-50 text-pink-700 rounded-full px-2 py-0.5 text-[10px] font-bold">↩ Returns</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <div className="text-xs font-bold text-slate-800">CFA {fmt(c.total_invoiced)}</div>
                        <div className="text-[9px] text-slate-500 uppercase font-semibold mt-1">Invoiced</div>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-2 text-center">
                        <div className="text-xs font-bold text-emerald-700">CFA {fmt(c.total_paid)}</div>
                        <div className="text-[9px] text-emerald-600 uppercase font-semibold mt-1">Paid</div>
                      </div>
                      <div className={`rounded-lg p-2 text-center ${isOverdue ? 'bg-red-50' : 'bg-emerald-50'}`}>
                        <div className={`text-xs font-bold ${isOverdue ? 'text-red-600' : 'text-emerald-600'}`}>CFA {fmt(balance)}</div>
                        <div className={`text-[9px] uppercase font-semibold mt-1 ${isOverdue ? 'text-red-500' : 'text-emerald-500'}`}>Balance</div>
                      </div>
                    </div>
                    {c.last_activity && (
                      <div className="mt-3 text-[10px] text-gray-400 border-t border-gray-100 pt-2">
                        Last activity: {fmtD(c.last_activity)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          VIEW 2 — CUSTOMER LEDGER (days grouped)
      ════════════════════════════════════════════════════════════════ */}
      {view === VIEW_LEDGER && selectedCustomer && (
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm">
            <button onClick={() => { setView(VIEW_DASHBOARD); fetchDashboard(); }} className="text-indigo-600 font-bold hover:underline bg-transparent border-none p-0 cursor-pointer">📊 Dashboard</button>
            <span className="text-gray-400">›</span>
            <span className="font-bold text-gray-900">{selectedCustomer.name}</span>
          </div>

          {/* Customer Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 m-0">{selectedCustomer.name}</h2>
                <p className="text-gray-500 text-sm mt-1">📞 {selectedCustomer.phone || 'No phone'} | {selectedCustomer.address || 'No address'}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {/* Date filter */}
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-200">
                  <span className="text-xs text-gray-500 font-semibold whitespace-nowrap">📅 From</span>
                  <input
                    type="date"
                    value={ledgerStartDate}
                    onChange={e => setLedgerStartDate(e.target.value)}
                    className="bg-transparent border-none text-sm text-gray-700 outline-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-500 font-semibold">To</span>
                  <input
                    type="date"
                    value={ledgerEndDate}
                    onChange={e => setLedgerEndDate(e.target.value)}
                    className="bg-transparent border-none text-sm text-gray-700 outline-none cursor-pointer"
                  />
                  {(ledgerStartDate || ledgerEndDate) && (
                    <button onClick={() => { setLedgerStartDate(''); setLedgerEndDate(''); }}
                      className="text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer text-sm font-bold p-1">✕</button>
                  )}
                </div>
                {/* Pay button */}
                {parseFloat(selectedCustomer.outstanding_balance) > 0 && (
                  <button onClick={() => {
                    setPayAllAmount(parseFloat(selectedCustomer.outstanding_balance).toFixed(2));
                    setIsPayAllOpen(true);
                  }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-bold shadow-sm transition">
                    💵 Pay Account
                  </button>
                )}
                <button onClick={() => printCustomerAccount(selectedCustomer, (ledgerData?.days || []).filter(d => {
                  const dt = new Date(d.purchase_date);
                  if (ledgerStartDate && dt < new Date(ledgerStartDate)) return false;
                  if (ledgerEndDate) { const e = new Date(ledgerEndDate); e.setHours(23,59,59,999); if (dt > e) return false; }
                  return true;
                }))}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg px-4 py-2 text-sm font-bold shadow-sm transition">
                  🖨️ Print Account
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[
                { label:'Total Purchased', val:`CFA ${fmt(selectedCustomer.total_invoiced)}`, color:'text-indigo-700', bg:'bg-indigo-50 border-indigo-100' },
                { label:'Total Paid',      val:`CFA ${fmt(selectedCustomer.total_paid)}`,     color:'text-emerald-700', bg:'bg-emerald-50 border-emerald-100' },
                { label:'Returns',         val:`CFA ${fmt(selectedCustomer.total_returns)}`,  color:'text-pink-700', bg:'bg-pink-50 border-pink-100' },
                { label:'Outstanding',     val:`CFA ${fmt(selectedCustomer.outstanding_balance)}`, color: parseFloat(selectedCustomer.outstanding_balance)>0 ? 'text-red-700' : 'text-emerald-700', bg: parseFloat(selectedCustomer.outstanding_balance)>0 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100' },
              ].map(s => (
                <div key={s.label} className={`rounded-lg p-3 border ${s.bg}`}>
                  <div className={`text-lg font-bold ${s.color}`}>{s.val}</div>
                  <div className="text-xs text-gray-500 font-semibold mt-1 uppercase">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm font-bold border border-red-200 mb-4">{error}</div>}
          {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>}

          {/* Days Timeline */}
          {!loading && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {(() => {
                const filteredDays = (ledgerData?.days || []).filter(d => {
                  const dt = new Date(d.purchase_date);
                  if (ledgerStartDate && dt < new Date(ledgerStartDate)) return false;
                  if (ledgerEndDate) { const e = new Date(ledgerEndDate); e.setHours(23,59,59,999); if (dt > e) return false; }
                  return true;
                });
                return (
                  <>
                    <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                      <span className="font-bold text-gray-700 text-sm">PURCHASE HISTORY ({filteredDays.length} days{(ledgerStartDate || ledgerEndDate) ? ' — filtered' : ''})</span>
                    </div>
                    {filteredDays.length === 0 && (
                      <div className="text-center py-16 text-gray-400 text-sm">
                        {ledgerStartDate || ledgerEndDate ? 'No records in selected date range.' : 'No purchase records found for this customer.'}
                      </div>
                    )}
                    <div className="divide-y divide-gray-100">
                      {filteredDays.map((day, idx) => {
                  const balance = parseFloat(day.day_balance);
                  return (
                    <div key={day.purchase_date}
                      className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer transition-colors group"
                      onClick={() => openDailyRecord(day, idx)}
                    >
                      {/* Date badge */}
                      <div className="bg-indigo-50 text-center rounded-lg px-3 py-2 min-w-[64px] shrink-0 border border-indigo-100">
                        <div className="text-xl font-bold text-indigo-700 leading-tight">{new Date(day.purchase_date).getDate()}</div>
                        <div className="text-[10px] text-indigo-500 font-bold uppercase mt-0.5">{new Date(day.purchase_date).toLocaleString('en', { month:'short' })}</div>
                        <div className="text-[10px] text-indigo-400 mt-0.5">{new Date(day.purchase_date).getFullYear()}</div>
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <div className="font-bold text-gray-800 text-sm">{fmtD(day.purchase_date)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {day.invoice_count} invoice{day.invoice_count > 1 ? 's' : ''} &nbsp;·&nbsp; {day.invoice_numbers}
                          {day.refund_count > 0 && <span className="ml-2 bg-pink-50 text-pink-700 rounded-full px-2 py-0.5 text-[10px] font-bold">↩ {day.refund_count} return{day.refund_count>1?'s':''}</span>}
                        </div>
                      </div>

                      {/* Amounts */}
                      <div className="flex gap-6 items-center">
                        <div className="text-right">
                          <div className="font-bold text-sm text-gray-800">CFA {fmt(day.day_total)}</div>
                          <div className="text-[10px] text-gray-400 uppercase font-semibold">Total</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-sm text-emerald-600">CFA {fmt(day.day_paid)}</div>
                          <div className="text-[10px] text-gray-400 uppercase font-semibold">Paid</div>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <div className={`font-bold text-sm ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>CFA {fmt(balance)}</div>
                          <div className="text-[10px] text-gray-400 uppercase font-semibold">Balance</div>
                        </div>
                        <div className="text-gray-300 group-hover:text-indigo-400 text-xl font-bold transition-colors">›</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
        </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          VIEW 3 — DAILY RECORD (invoice detail)
      ════════════════════════════════════════════════════════════════ */}
      {view === VIEW_DAILY && dailyData && (
        <div>
          {/* Breadcrumb + nav */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <button onClick={() => { setView(VIEW_DASHBOARD); fetchDashboard(); }} className="text-indigo-600 font-bold hover:underline bg-transparent border-none p-0 cursor-pointer">📊 Dashboard</button>
              <span className="text-gray-400">›</span>
              <button onClick={() => setView(VIEW_LEDGER)} className="text-indigo-600 font-bold hover:underline bg-transparent border-none p-0 cursor-pointer">{selectedCustomer?.name}</button>
              <span className="text-gray-400">›</span>
              <span className="font-bold text-gray-900">{fmtD(dailyData.date)}</span>
            </div>
            {/* Prev / Next */}
            <div className="flex gap-2 items-center">
              <span className="text-xs text-gray-400">← → keys to navigate</span>
              <button disabled={selectedDayIdx >= (ledgerData?.days?.length||0)-1} onClick={() => goToDay(selectedDayIdx+1)}
                className={`px-3 py-1.5 rounded-lg border font-bold text-xs transition ${selectedDayIdx >= (ledgerData?.days?.length||0)-1 ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 shadow-sm cursor-pointer'}`}>
                ← Previous Day
              </button>
              <button disabled={selectedDayIdx <= 0} onClick={() => goToDay(selectedDayIdx-1)}
                className={`px-3 py-1.5 rounded-lg border font-bold text-xs transition ${selectedDayIdx <= 0 ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 shadow-sm cursor-pointer'}`}>
                Next Day →
              </button>
            </div>
          </div>

          {/* Day summary bar */}
          <div className="bg-gray-50 rounded-xl px-5 py-4 border border-gray-200 mb-6 flex flex-wrap items-center gap-6 shadow-sm">
            <div className="font-bold text-lg text-gray-800">{fmtD(dailyData.date)}</div>
            <div className="text-gray-500 text-sm font-medium">{(dailyData.invoices||[]).length} invoice{(dailyData.invoices||[]).length!==1?'s':''}</div>
            <div className="ml-auto flex items-center gap-5">
              <span className="text-gray-800 text-sm font-bold">Total: CFA {fmt((dailyData.invoices||[]).reduce((s,i)=>s+parseFloat(i.total_amount||0),0))}</span>
              <span className="text-emerald-600 text-sm font-bold">Paid: CFA {fmt((dailyData.invoices||[]).reduce((s,i)=>s+parseFloat(i.amount_paid||0),0))}</span>
              {parseFloat((dailyData.invoices||[]).reduce((s,i)=>s+parseFloat(i.total_amount||0),0) - (dailyData.invoices||[]).reduce((s,i)=>s+parseFloat(i.amount_paid||0),0)) > 0 && (
                  <button onClick={() => {
                      setPayAllAmount(((dailyData.invoices||[]).reduce((s,i)=>s+parseFloat(i.total_amount||0),0) - (dailyData.invoices||[]).reduce((s,i)=>s+parseFloat(i.amount_paid||0),0)).toFixed(2));
                      setIsPayAllOpen(true);
                  }} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-xs font-bold transition shadow-sm border-none cursor-pointer">
                      Pay All Outstanding
                  </button>
              )}
            </div>
          </div>

          {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm font-bold border border-red-200 mb-4">{error}</div>}

          {/* Invoices */}
          {(dailyData.invoices || []).map(inv => (
            <div key={inv.id} className="bg-white rounded-xl border border-gray-200 mb-6 overflow-hidden shadow-sm">
              {/* Invoice header */}
              <div className="bg-slate-50 border-b border-gray-200 px-5 py-4 flex justify-between items-center flex-wrap gap-4">
                <div>
                  <div className="text-gray-500 text-[10px] font-bold tracking-widest uppercase mb-1">Invoice</div>
                  <div className="text-gray-900 font-black text-xl leading-none">{inv.invoice_number}</div>
                  <div className="text-gray-500 text-xs mt-1.5 font-medium">{fmtD(inv.created_at)} at {fmtT(inv.created_at)} · {inv.payment_method||'Cash'} · by {inv.user_name||'—'}</div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full border ${inv.status==='Paid'?'bg-emerald-100 text-emerald-800 border-emerald-200':inv.status==='Partial'?'bg-amber-100 text-amber-800 border-amber-200':'bg-red-100 text-red-800 border-red-200'}`}>
                    {inv.status}
                  </span>
                  {isManager && (
                    <button onClick={() => openEdit(inv)} className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg px-3 py-1.5 text-xs font-bold transition shadow-sm">
                      ✏️ Edit
                    </button>
                  )}
                  {(inv.status === 'Unpaid' || inv.status === 'Partial') && (
                    <button onClick={() => { setPayInvoice(inv); setPayAmount((inv.total_amount - inv.amount_paid).toFixed(2)); setIsPayOpen(true); }}
                      className="bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 rounded-lg px-3 py-1.5 text-xs font-bold transition shadow-sm">
                      💵 Pay
                    </button>
                  )}
                  <button onClick={() => openReturn(inv)}
                    className="bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 rounded-lg px-3 py-1.5 text-xs font-bold transition shadow-sm">
                    ↩️ Return
                  </button>
                  <button onClick={() => printInvoice(inv, selectedCustomer)}
                    className="bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 rounded-lg px-3 py-1.5 text-xs font-bold transition shadow-sm">
                    🖨️ Print
                  </button>
                  {isAdmin && (
                    <button onClick={() => { setDeleteInvId(inv.id); setDeleteOpen(true); }}
                      className="bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 rounded-lg px-3 py-1.5 text-xs font-bold transition shadow-sm">
                      🗑️
                    </button>
                  )}
                </div>
              </div>

              {/* Items table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {['Product','Qty','Unit','Unit Price','Discount','Line Total'].map(h => (
                        <th key={h} className={`py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide ${h==='Product'?'text-left':'text-right'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(inv.items || []).map((item, i) => (
                      <tr key={item.id} className={`border-b border-gray-100 ${i%2===0?'bg-white':'bg-gray-50'}`}>
                        <td className="py-2.5 px-4 font-bold text-gray-900 text-sm">{item.product_name}</td>
                        <td className="py-2.5 px-4 text-right font-bold text-indigo-700">{fmt(item.quantity)}</td>
                        <td className="py-2.5 px-4 text-right text-gray-500 text-xs">{item.unit||'—'}</td>
                        <td className="py-2.5 px-4 text-right text-gray-600">CFA {fmt(item.unit_price)}</td>
                        <td className={`py-2.5 px-4 text-right text-xs ${item.discount>0?'text-red-600':'text-gray-400'}`}>{item.discount>0?`-CFA ${fmt(item.discount)}`:'—'}</td>
                        <td className="py-2.5 px-4 text-right font-black text-gray-900">CFA {fmt(item.price_ttc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Invoice totals */}
              <div className="p-5 flex justify-between items-start border-t border-gray-100 flex-wrap gap-4">
                <div>
                  {inv.note && <div className="text-xs text-gray-500 bg-gray-50 p-2.5 rounded-lg border-l-4 border-indigo-500 max-w-[340px]"><b>Note:</b> {inv.note}</div>}
                </div>
                <div className="text-right min-w-[200px]">
                  {inv.tva_rate > 0 && <div className="text-xs text-gray-500 mb-1">HT: CFA {fmt(inv.total_ht)} · TVA {inv.tva_rate}%: CFA {fmt(inv.total_tva)}</div>}
                  {inv.discount_total > 0 && <div className="text-xs text-red-600 mb-1">Discount: -CFA {fmt(inv.discount_total)}</div>}
                  <div className="text-xl font-black text-gray-900">Total: CFA {fmt(inv.total_amount)}</div>
                  <div className="text-sm font-bold text-emerald-600 mt-1">Paid: CFA {fmt(inv.amount_paid)}</div>
                  {parseFloat(inv.total_amount) - parseFloat(inv.amount_paid) > 0.01 && (
                    <div className="text-sm font-bold text-red-600 mt-0.5">Outstanding: CFA {fmt(parseFloat(inv.total_amount)-parseFloat(inv.amount_paid))}</div>
                  )}
                </div>
              </div>

              {/* Refunds accordion */}
              {(inv.refunds || []).length > 0 && (
                <div className="bg-pink-50 border-t border-pink-200 px-5 py-3">
                  <div className="font-bold text-pink-700 text-xs mb-2 tracking-wide uppercase">↩️ Return Records</div>
                  {inv.refunds.map((r, ri) => (
                    <div key={ri} className="text-xs text-pink-900 mb-1 flex gap-4">
                      <span>{fmtD(r.created_at)}</span>
                      <span>{r.product_name}: {fmt(r.qty_refunded)} units</span>
                      <span className="font-bold">CFA {fmt(r.refund_amount)} refunded</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Audit trail */}
              {(inv.audit_trail || []).length > 0 && (
                <details className="border-t border-gray-100 group">
                  <summary className="px-5 py-2.5 cursor-pointer text-xs font-bold text-gray-500 select-none bg-gray-50 hover:bg-gray-100 transition">
                    📋 Audit Trail ({inv.audit_trail.length} entries)
                  </summary>
                  <div className="px-5 py-3 pb-4 bg-white">
                    {inv.audit_trail.map((a, ai) => (
                      <div key={ai} className="text-xs text-gray-500 py-1.5 border-b border-gray-50 flex gap-4 flex-wrap last:border-0">
                        <span className={`font-bold ${a.action_type==='Delete'?'text-red-600':a.action_type==='Refund'?'text-pink-600':'text-sky-600'}`}>{a.action_type}</span>
                        <span>{fmtD(a.logged_at)} {fmtT(a.logged_at)}</span>
                        <span>by {a.user_name || '—'}</span>
                        {a.reason && <span className="text-gray-900 font-bold">Reason: {a.reason}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          EDIT INVOICE MODAL
      ════════════════════════════════════════════════════════════════ */}
      {editOpen && editInv && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={e => { if (e.target===e.currentTarget) setEditOpen(false); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:700, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
            <div className="bg-gradient-to-r from-indigo-800 to-indigo-900 px-6 py-5 rounded-t-2xl flex justify-between items-center">
              <div>
                <div style={{ color:'#a5b4fc', fontSize:11, fontWeight:600 }}>EDIT INVOICE</div>
                <div style={{ color:'#fff', fontWeight:900, fontSize:17 }}>{editInv.invoice_number}</div>
              </div>
              <button onClick={() => setEditOpen(false)} style={{ background:'rgba(255,255,255,.15)', color:'#fff', border:'none', borderRadius:8, width:32, height:32, fontSize:18, cursor:'pointer' }}>×</button>
            </div>

            <form onSubmit={handleEditSave} style={{ padding:24 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#64748b', marginBottom:12, textTransform:'uppercase', letterSpacing:.5 }}>Line Items</div>
              {editItems.map((item, i) => (
                <div key={i} style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center', background:'#f8fafc', borderRadius:10, padding:'10px 14px', flexWrap:'wrap' }}>
                  <div style={{ flex:2, minWidth:140, fontSize:13, fontWeight:600, color:'#0f172a' }}>{item.product_name}</div>
                  <div style={{ display:'flex', gap:8, flex:3, minWidth:200, flexWrap:'wrap' }}>
                    <div style={{ flex:1, minWidth:80 }}>
                      <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>QTY</label>
                      <input type="number" min="0.01" step="0.01" required value={item.quantity}
                        onChange={e => { const ni=[...editItems]; ni[i]={...ni[i],quantity:e.target.value}; setEditItems(ni); }}
                        style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                      />
                    </div>
                    <div style={{ flex:1, minWidth:100 }}>
                      <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>UNIT PRICE</label>
                      <input type="number" min="0" step="0.01" required value={item.unit_price}
                        onChange={e => { const ni=[...editItems]; ni[i]={...ni[i],unit_price:e.target.value}; setEditItems(ni); }}
                        style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                      />
                    </div>
                    <div style={{ flex:1, minWidth:80 }}>
                      <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>DISCOUNT</label>
                      <input type="number" min="0" step="0.01" value={item.discount||0}
                        onChange={e => { const ni=[...editItems]; ni[i]={...ni[i],discount:e.target.value}; setEditItems(ni); }}
                        style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:800, color:'#1e1b4b', minWidth:100, textAlign:'right' }}>
                    CFA {fmt((parseFloat(item.quantity)||0)*(parseFloat(item.unit_price)||0)-(parseFloat(item.discount)||0))}
                  </div>
                  <button type="button" onClick={() => setEditItems(editItems.filter((_,j)=>j!==i))}
                    style={{ background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:13, fontWeight:700 }}>✕</button>
                </div>
              ))}

              {/* Add product */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:12, color:'#64748b', marginBottom:8, textTransform:'uppercase' }}>Add Product</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <input value={prodSearch} onChange={e => setProdSearch(e.target.value)} placeholder="Search product..."
                    style={{ flex:1, padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', minWidth:160 }}
                  />
                  <select onChange={e => {
                    const p = products.find(x => x.id === parseInt(e.target.value));
                    if (p) { setEditItems([...editItems, { product_id:p.id, product_name:p.name, unit:p.unit, quantity:1, unit_price:p.price||0, discount:0 }]); e.target.value=''; setProdSearch(''); }
                  }} style={{ flex:2, padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', background:'#fff', minWidth:180 }}>
                    <option value="">— Select product to add —</option>
                    {products.filter(p => !prodSearch || p.name.toLowerCase().includes(prodSearch.toLowerCase())).map(p => (
                      <option key={p.id} value={p.id}>{p.name} (CFA {fmt(p.price)})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Note */}
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:6, textTransform:'uppercase' }}>Invoice Note</label>
                <textarea value={editNote} onChange={e => setEditNote(e.target.value)} rows={2}
                  style={{ width:'100%', padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }}
                />
              </div>

              {/* Reason (required) */}
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#dc2626', display:'block', marginBottom:6, textTransform:'uppercase' }}>Reason for Edit *</label>
                <input required value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="Why are you editing this invoice?"
                  style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #fca5a5', borderRadius:8, fontSize:13, outline:'none', background:'#fff9f9', boxSizing:'border-box' }}
                />
              </div>

              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-700 to-indigo-600 text-white rounded-lg p-3 text-sm font-bold shadow-sm cursor-pointer border-none hover:shadow-md transition">
                  {loading ? 'Saving…' : '💾 Save Changes & Sync Inventory'}
                </button>
                <button type="button" onClick={() => setEditOpen(false)}
                  style={{ padding:'12px 20px', background:'#f1f5f9', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', color:'#64748b' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          RETURN MODAL
      ════════════════════════════════════════════════════════════════ */}
      {returnOpen && returnInv && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={e => { if (e.target===e.currentTarget) setReturnOpen(false); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:600, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-5 rounded-t-2xl flex justify-between items-center">
              <div>
                <div style={{ color:'#e9d5ff', fontSize:11, fontWeight:600 }}>PROCESS RETURN</div>
                <div style={{ color:'#fff', fontWeight:900, fontSize:17 }}>{returnInv.invoice_number}</div>
              </div>
              <button onClick={() => setReturnOpen(false)} style={{ background:'rgba(255,255,255,.15)', color:'#fff', border:'none', borderRadius:8, width:32, height:32, fontSize:18, cursor:'pointer' }}>×</button>
            </div>

            <form onSubmit={handleReturnSave} style={{ padding:24 }}>
              {/* Return type */}
              <div style={{ display:'flex', gap:10, marginBottom:20 }}>
                {['partial','full'].map(t => (
                  <button type="button" key={t} onClick={() => setReturnType(t)}
                    style={{ flex:1, padding:'10px', borderRadius:10, border:'2px solid', borderColor: returnType===t?'#7c3aed':'#e2e8f0', background: returnType===t?'#ede9fe':'#fff', color: returnType===t?'#5b21b6':'#64748b', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                    {t === 'full' ? '↩️ Full Return' : '📦 Partial Return'}
                  </button>
                ))}
              </div>

              {/* Items */}
              {returnType === 'partial' && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontWeight:700, fontSize:12, color:'#64748b', marginBottom:10, textTransform:'uppercase' }}>Select Return Quantities</div>
                  {returnItems.map((item, i) => (
                    <div key={i} style={{ display:'flex', gap:12, marginBottom:10, alignItems:'center', background:'#faf5ff', borderRadius:10, padding:'10px 14px' }}>
                      <div style={{ flex:1, fontSize:13, fontWeight:600, color:'#1e293b' }}>{item.product_name}</div>
                      <div style={{ fontSize:12, color:'#64748b' }}>Max: {fmt(item.quantity)} {item.unit||''}</div>
                      <input type="number" min="0" step="0.01" max={item.quantity} value={item.qty_return}
                        onChange={e => { const ni=[...returnItems]; ni[i]={...ni[i],qty_return:e.target.value}; setReturnItems(ni); }}
                        style={{ width:90, padding:'7px 10px', border:'1.5px solid #d8b4fe', borderRadius:8, fontSize:13, outline:'none', textAlign:'right' }}
                      />
                    </div>
                  ))}
                  {/* Preview total */}
                  <div style={{ background:'#ede9fe', borderRadius:10, padding:'10px 14px', fontSize:13, fontWeight:700, color:'#5b21b6', textAlign:'right' }}>
                    Estimated refund: CFA {fmt(returnItems.reduce((s,it) => {
                      const unitTTC = parseFloat(it.quantity)>0 ? parseFloat(it.price_ttc)/parseFloat(it.quantity) : 0;
                      return s + unitTTC * (parseFloat(it.qty_return)||0);
                    }, 0))}
                  </div>
                </div>
              )}

              {returnType === 'full' && (
                <div style={{ background:'#fef3c7', border:'1.5px solid #fcd34d', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13, color:'#92400e', fontWeight:600 }}>
                  ⚠️ This will return ALL items from {returnInv.invoice_number} and restore full stock quantities.
                </div>
              )}

              {/* Reason */}
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#7c3aed', display:'block', marginBottom:6, textTransform:'uppercase' }}>Return Reason *</label>
                <input required value={returnReason} onChange={e => setReturnReason(e.target.value)} placeholder="Why are the goods being returned?"
                  style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #d8b4fe', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                />
              </div>

              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg p-3 text-sm font-bold shadow-sm cursor-pointer border-none hover:shadow-md transition">
                  {loading ? 'Processing…' : '↩️ Confirm Return & Restore Stock'}
                </button>
                <button type="button" onClick={() => setReturnOpen(false)}
                  style={{ padding:'12px 20px', background:'#f1f5f9', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', color:'#64748b' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          DELETE CONFIRM MODAL
      ════════════════════════════════════════════════════════════════ */}
      {deleteOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, maxWidth:420, width:'100%', textAlign:'center', boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>⚠️</div>
            <h3 style={{ fontSize:18, fontWeight:900, color:'#1e293b', margin:'0 0 8px' }}>Delete Invoice?</h3>
            <p style={{ color:'#64748b', fontSize:14, margin:'0 0 24px' }}>This action will permanently delete the invoice and restore all stock quantities. This cannot be undone.</p>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={handleDelete} disabled={loading}
                style={{ flex:1, background:'#dc2626', color:'#fff', border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:800, cursor:'pointer' }}>
                {loading ? 'Deleting…' : '🗑️ Yes, Delete & Restore Stock'}
              </button>
              <button onClick={() => setDeleteOpen(false)}
                style={{ padding:'12px 20px', background:'#f1f5f9', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', color:'#64748b' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          PAY INVOICE MODAL
      ════════════════════════════════════════════════════════════════ */}
      {isPayOpen && payInvoice && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={e => { if (e.target===e.currentTarget) setIsPayOpen(false); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:460, boxShadow:'0 24px 80px rgba(0,0,0,.35)', overflow:'hidden' }}>
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-5 flex justify-between items-center">
              <div>
                <div style={{ color:'#d1fae5', fontSize:11, fontWeight:600, letterSpacing:.5 }}>PAY INVOICE</div>
                <div style={{ color:'#fff', fontWeight:900, fontSize:17 }}>{payInvoice.invoice_number}</div>
              </div>
              <button onClick={() => setIsPayOpen(false)} style={{ background:'rgba(255,255,255,.2)', color:'#fff', border:'none', borderRadius:8, width:32, height:32, fontSize:18, cursor:'pointer' }}>×</button>
            </div>
            <form onSubmit={async e => {
              e.preventDefault();
              const amount = parseFloat(payAmount);
              if (!amount || amount <= 0) return alert('Enter a valid amount.');
              setLoading(true);
              try {
                const res = await authFetch(`/api/invoices?action=pay&id=${payInvoice.id}`, {
                  method: 'POST',
                  body: JSON.stringify({ amount, note: `Paid via ${payMethod}` })
                });
                if (res.ok) {
                  setIsPayOpen(false); setPayAmount('');
                  showToast(`✅ Payment of CFA ${fmt(amount)} recorded`);
                  refreshDaily(); fetchDashboard();
                } else {
                  const err = await res.json();
                  alert(err.message || 'Payment failed');
                }
              } catch { alert('Network error while paying.'); } finally { setLoading(false); }
            }} style={{ padding:24 }}>
              <div style={{ background:'#f0fdf4', borderRadius:10, padding:'14px 16px', marginBottom:16, fontSize:13 }}>
                <div>Invoice: <strong>{payInvoice.invoice_number}</strong></div>
                <div style={{ marginTop:4 }}>Total: <strong>CFA {fmt(payInvoice.total_amount)}</strong> &nbsp;|&nbsp; Paid: <strong style={{ color:'#059669' }}>CFA {fmt(payInvoice.amount_paid)}</strong></div>
                <div style={{ marginTop:4 }}>Remaining: <strong style={{ color:'#dc2626', fontSize:16 }}>CFA {fmt(parseFloat(payInvoice.total_amount) - parseFloat(payInvoice.amount_paid))}</strong></div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>PAYMENT METHOD</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                  style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', background:'#fff' }}>
                  <option>Cash</option><option>Visa / Card</option><option>Bank Transfer</option><option>Mobile Money</option>
                </select>
              </div>
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>AMOUNT TO PAY</label>
                <input type="number" step="0.01" min="0.01" required value={payAmount} onChange={e => setPayAmount(e.target.value)}
                  style={{ width:'100%', padding:'12px 14px', border:'2px solid #34d399', borderRadius:10, fontSize:20, fontWeight:800, outline:'none', boxSizing:'border-box', textAlign:'right', color:'#065f46' }} />
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" disabled={loading}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-lg p-3 text-sm font-bold shadow-sm cursor-pointer border-none hover:shadow-md transition">
                  {loading ? 'Processing…' : '💵 Submit Payment'}
                </button>
                <button type="button" onClick={() => setIsPayOpen(false)}
                  style={{ padding:'12px 20px', background:'#f1f5f9', border:'none', borderRadius:10, fontSize:14, cursor:'pointer', color:'#64748b' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          PAY ALL / ACCOUNT PAYMENT MODAL
      ════════════════════════════════════════════════════════════════ */}
      {isPayAllOpen && selectedCustomer && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={e => { if (e.target===e.currentTarget) setIsPayAllOpen(false); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:500, boxShadow:'0 24px 80px rgba(0,0,0,.35)', overflow:'hidden' }}>
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-5 flex justify-between items-center">
              <div>
                <div style={{ color:'#d1fae5', fontSize:11, fontWeight:600, letterSpacing:.5 }}>ACCOUNT PAYMENT</div>
                <div style={{ color:'#fff', fontWeight:900, fontSize:17 }}>{selectedCustomer.name}</div>
              </div>
              <button onClick={() => setIsPayAllOpen(false)} style={{ background:'rgba(255,255,255,.2)', color:'#fff', border:'none', borderRadius:8, width:32, height:32, fontSize:18, cursor:'pointer' }}>×</button>
            </div>
            <form onSubmit={async e => {
              e.preventDefault();
              const amount = parseFloat(payAllAmount);
              if (!amount || amount <= 0) return alert('Enter a valid amount.');
              setLoading(true);
              try {
                const res = await authFetch('/api/customer_payments', {
                  method: 'POST',
                  body: JSON.stringify({ customer_id: selectedCustomer.id, amount, payment_method: payAllMethod, note: `Account payment via ${payAllMethod}` })
                });
                if (res.ok) {
                  setIsPayAllOpen(false); setPayAllAmount('');
                  showToast(`✅ CFA ${fmt(amount)} payment applied to ${selectedCustomer.name}'s account`);
                  // Refresh ledger & dashboard
                  authFetch(`/api/sales?customer_id=${selectedCustomer.id}`)
                    .then(r => r.json()).then(d => { setLedgerData(d); setSelectedCustomer(d.customer); }).catch(() => {});
                  fetchDashboard();
                } else {
                  const err = await res.json();
                  alert(err.message || 'Payment failed');
                }
              } catch { alert('Network error while paying.'); } finally { setLoading(false); }
            }} style={{ padding:24 }}>
              <div style={{ background:'#f0fdf4', borderRadius:10, padding:'16px', marginBottom:16 }}>
                <div style={{ fontSize:13, color:'#065f46' }}>Customer: <strong>{selectedCustomer.name}</strong></div>
                <div style={{ fontSize:13, color:'#065f46', marginTop:4 }}>Outstanding Balance: <strong style={{ fontSize:22, color:'#dc2626' }}>CFA {fmt(selectedCustomer.outstanding_balance)}</strong></div>
                <div style={{ fontSize:11, color:'#6b7280', marginTop:6 }}>Payments are applied to the oldest unpaid invoices first.</div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>PAYMENT METHOD</label>
                <select value={payAllMethod} onChange={e => setPayAllMethod(e.target.value)}
                  style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', background:'#fff' }}>
                  <option>Cash</option><option>Visa / Card</option><option>Bank Transfer</option><option>Mobile Money</option>
                </select>
              </div>
              <div style={{ marginBottom:8 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:6 }}>AMOUNT RECEIVED</label>
                <input type="number" step="0.01" min="0.01" required value={payAllAmount} onChange={e => setPayAllAmount(e.target.value)}
                  style={{ width:'100%', padding:'12px 14px', border:'2px solid #34d399', borderRadius:10, fontSize:22, fontWeight:900, outline:'none', boxSizing:'border-box', textAlign:'right', color:'#065f46' }} />
              </div>
              {parseFloat(payAllAmount) > 0 && parseFloat(selectedCustomer.outstanding_balance) > 0 && (
                <div style={{ background: parseFloat(payAllAmount) >= parseFloat(selectedCustomer.outstanding_balance) ? '#d1fae5' : '#fef3c7',
                  borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, fontWeight:700,
                  color: parseFloat(payAllAmount) >= parseFloat(selectedCustomer.outstanding_balance) ? '#065f46' : '#92400e' }}>
                  {parseFloat(payAllAmount) >= parseFloat(selectedCustomer.outstanding_balance)
                    ? `✅ Full balance cleared + CFA ${fmt(parseFloat(payAllAmount) - parseFloat(selectedCustomer.outstanding_balance))} change`
                    : `Remaining after payment: CFA ${fmt(parseFloat(selectedCustomer.outstanding_balance) - parseFloat(payAllAmount))}`
                  }
                </div>
              )}
              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" disabled={loading}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-lg p-3 text-sm font-bold shadow-sm cursor-pointer border-none hover:shadow-md transition">
                  {loading ? 'Processing…' : '💵 Record Payment'}
                </button>
                <button type="button" onClick={() => setIsPayAllOpen(false)}
                  style={{ padding:'12px 20px', background:'#f1f5f9', border:'none', borderRadius:10, fontSize:14, cursor:'pointer', color:'#64748b' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        details summary::-webkit-details-marker { display:none; }
      `}</style>
    </div>
  );
};

export default Sales;
